import { FunctionsError } from "firebase/functions";

// Only these read-only calls can share requests, observe a cooldown, or
// retry once with a refreshed ID token after a 401.
// Mutations (especially paid generation and recovery commands) never enter it.
const READ_CALLABLES = new Set([
  "unityGetFacilitatorProgressSnapshot",
  "unityGetActiveContentGeneration",
  "unityGetProgramContentManager",
  "unityGetProgramTextbookStatus",
  "unityDownloadProgramTextbook",
  "unityGetProgramContentRun",
  "unityGetProgramTranslationManager",
  "unityGetProgramTranslationRun",
  "unityGetContentJob",
  "unityGetOutcomeWorkspace",
  "unityGetOutcomeSection",
  "unityDownloadLearningPackDocuments",
  "unityRenderOutcomePdf",
  // Source-program generation can run long enough for its ID token to expire.
  // These operations only inspect saved state or render saved guide documents.
  "unityGetProgramBuild",
  "unityListProgramBuilds",
  "unityGetProgramBuildSourcePreview",
  "unityGetProgramBuildRevision",
  "unityGetSourceProgramGuides",
  // Read right after sign-in, when a just-minted ID token can still be
  // momentarily rejected by the server before it's fully propagated.
  "getAccessContext",
]);
const userScopes = new WeakMap();
const MAX_RETRY_MS = 24 * 60 * 60 * 1000;

export function isUnityStatusRead(name) {
  return READ_CALLABLES.has(name);
}

export function readRetryDelayMs(error, now = Date.now()) {
  const code = String(error?.code || "").replace(/^functions\//, "");
  if (code !== "resource-exhausted") return 0;
  const retryAt = Number(error?.details?.retryAtMs);
  if (Number.isFinite(retryAt) && retryAt > 0) {
    return Math.min(MAX_RETRY_MS, Math.max(0, retryAt - now));
  }
  const seconds = Number(error?.details?.retryAfterSeconds);
  // A domain-specific resource-exhausted error is not necessarily a rate
  // limit (e.g. an active programme conflict). Keep its diagnosis intact.
  if (error?.details?.reason &&
      error.details.reason !== "unity-status-read-throttled" &&
      !(Number.isFinite(seconds) && seconds > 0)) return 0;
  return Number.isFinite(seconds) && seconds > 0 ?
    Math.min(MAX_RETRY_MS, seconds * 1000) : 30000;
}

function cooldownError(retryAtMs) {
  return new FunctionsError("resource-exhausted",
    "Generation status updates are temporarily limited.", {
      reason: "unity-status-read-throttled",
      retryAtMs,
      retryAfterSeconds: Math.max(1, Math.ceil((retryAtMs - Date.now()) / 1000)),
    });
}

export function coordinateUnityRead({ user, idToken, url, name, payload, invoke, authRetry = false }) {
  if (!isUnityStatusRead(name) || !user || !idToken) return invoke();

  // A user object alone is not a tenant boundary: refreshed claims can change
  // its active tenant. Never share a response or cooldown across ID tokens or
  // callable origins. No results, tokens, or errors are persisted to storage.
  let scope = userScopes.get(user);
  const origin = url.slice(0, url.lastIndexOf("/"));
  if (!scope || scope.idToken !== idToken || scope.origin !== origin) {
    scope = {
      idToken,
      origin,
      pending: new Map(),
      cooldowns: new Map(),
    };
    userScopes.set(user, scope);
  }
  // A final auth retry must never join a first attempt that can refresh again.
  // Keep its one-replay budget intact even when another view obtains the new
  // token and starts the same read concurrently.
  const key = `${authRetry ? "auth-retry:" : ""}${name}:${JSON.stringify(payload)}`;
  const existing = scope.pending.get(key);
  if (existing) return existing;
  // The server's read limit applies across payloads (e.g. inventory pages),
  // so a different job or cursor must not bypass an endpoint's 429 cooldown.
  const retryAt = scope.cooldowns.get(name) || 0;
  if (retryAt > Date.now()) return Promise.reject(cooldownError(retryAt));
  scope.cooldowns.delete(name);

  const request = Promise.resolve().then(invoke).catch((error) => {
    const delay = readRetryDelayMs(error);
    if (!delay) throw error;
    const nextRetryAt = Date.now() + delay;
    scope.cooldowns.set(name, Math.max(scope.cooldowns.get(name) || 0, nextRetryAt));
    throw cooldownError(nextRetryAt);
  });
  scope.pending.set(key, request);
  const clear = () => {
    if (scope.pending.get(key) === request) scope.pending.delete(key);
  };
  request.then(clear, clear);
  return request;
}
